import { useMemo } from 'react';
import Card, { CardHeader, CardBody } from '../ui/Card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface BalanceSheetData {
  totalAssets?: string | null;
  currentAssets?: string | null;
  cash?: string | null;
  marketableSecurities?: string | null;
  accountsReceivable?: string | null;
  inventory?: string | null;
  propertyPlantEquipment?: string | null;
  intangibleAssets?: string | null;
  longTermInvestments?: string | null;
  totalLiabilities?: string | null;
  currentLiabilities?: string | null;
  accountsPayable?: string | null;
  shortTermDebt?: string | null;
  currentPortionLongTermDebt?: string | null;
  longTermDebt?: string | null;
  deferredRevenue?: string | null;
  pensionLiabilities?: string | null;
  shareholdersEquity?: string | null;
  commonStock?: string | null;
  retainedEarnings?: string | null;
  treasuryStock?: string | null;
}

interface Report {
  reportId: string;
  period: string;
  type: string;
  balanceSheet?: BalanceSheetData;
}

interface ComparativeBalanceSheetTableProps {
  reports: Report[];
  mode: 'single' | 'annual' | 'quarterly';
  maxPeriods: number;
}

interface ChangeIndicator {
  percentage: number;
  direction: 'up' | 'down' | 'neutral';
  color: string;
}

const parseCurrency = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const cleaned = value.replace(/[$,\s]/g, '');
  let multiplier = 1;
  if (cleaned.includes('B')) multiplier = 1_000_000_000;
  else if (cleaned.includes('M')) multiplier = 1_000_000;
  else if (cleaned.includes('K')) multiplier = 1_000;
  const num = parseFloat(cleaned.replace(/[BMK]/g, ''));
  return isNaN(num) ? null : num * multiplier;
};

const calculateChange = (current: string | null | undefined, previous: string | null | undefined): ChangeIndicator | null => {
  const currentVal = parseCurrency(current);
  const previousVal = parseCurrency(previous);
  if (currentVal === null || previousVal === null || previousVal === 0) return null;
  const change = ((currentVal - previousVal) / Math.abs(previousVal)) * 100;
  return {
    percentage: change,
    direction: Math.abs(change) < 0.5 ? 'neutral' : change > 0 ? 'up' : 'down',
    color: Math.abs(change) < 0.5 ? 'text-neutral-600' : change > 0 ? 'text-success-600' : 'text-error-600'
  };
};

const formatValue = (value: string | null | undefined): string => {
  if (!value) return 'N/A';
  return value;
};

// Check if a field has at least one non-null value across all reports
const hasAnyValue = (reports: Report[], field: string): boolean => {
  return reports.some(r => {
    const val = r.balanceSheet?.[field as keyof BalanceSheetData];
    return val != null && val !== '';
  });
};

export default function ComparativeBalanceSheetTable({ reports, mode, maxPeriods }: ComparativeBalanceSheetTableProps) {
  const displayedReports = useMemo(() => {
    let filtered: Report[];
    if (mode === 'annual') {
      filtered = reports.filter(r => r.type === 'Annual' || r.period.includes('FY'));
    } else if (mode === 'quarterly') {
      filtered = reports.filter(r => r.type === 'Quarterly' || r.period.match(/Q[1-4]/));
    } else {
      filtered = reports.slice(0, 1);
    }
    return filtered.slice(0, maxPeriods);
  }, [reports, mode, maxPeriods]);

  // Define all rows — items with null fields are section headers
  const allRows = useMemo(() => [
    { type: 'header',   label: 'ASSETS',                              field: null },
    { type: 'category', label: 'Current Assets',                      field: 'currentAssets' },
    { type: 'item',     label: 'Cash & Cash Equivalents',             field: 'cash' },
    { type: 'item',     label: 'Marketable Securities',               field: 'marketableSecurities' },
    { type: 'item',     label: 'Accounts Receivable',                 field: 'accountsReceivable' },
    { type: 'item',     label: 'Inventory',                           field: 'inventory' },
    { type: 'category', label: 'Non-Current Assets',                  field: null },
    { type: 'item',     label: 'Property, Plant & Equipment',         field: 'propertyPlantEquipment' },
    { type: 'item',     label: 'Intangible Assets',                   field: 'intangibleAssets' },
    { type: 'item',     label: 'Long-term Investments',               field: 'longTermInvestments' },
    { type: 'total',    label: 'Total Assets',                        field: 'totalAssets' },
    { type: 'header',   label: 'LIABILITIES',                         field: null },
    { type: 'category', label: 'Current Liabilities',                 field: 'currentLiabilities' },
    { type: 'item',     label: 'Accounts Payable',                    field: 'accountsPayable' },
    { type: 'item',     label: 'Short-term Debt',                     field: 'shortTermDebt' },
    { type: 'item',     label: 'Current Portion of Long-term Debt',   field: 'currentPortionLongTermDebt' },
    { type: 'item',     label: 'Deferred Revenue',                    field: 'deferredRevenue' },
    { type: 'category', label: 'Non-Current Liabilities',             field: null },
    { type: 'item',     label: 'Long-term Debt',                      field: 'longTermDebt' },
    { type: 'item',     label: 'Pension Liabilities',                 field: 'pensionLiabilities' },
    { type: 'total',    label: 'Total Liabilities',                   field: 'totalLiabilities' },
    { type: 'header',   label: "SHAREHOLDERS' EQUITY",                field: null },
    { type: 'item',     label: 'Common Stock',                        field: 'commonStock' },
    { type: 'item',     label: 'Retained Earnings',                   field: 'retainedEarnings' },
    { type: 'item',     label: 'Treasury Stock',                      field: 'treasuryStock' },
    { type: 'total',    label: "Total Shareholders' Equity",          field: 'shareholdersEquity' },
  ], []);

  // Filter out item/category rows that have NO data across all displayed reports
  const rows = useMemo(() => {
    return allRows.filter(row => {
      if (!row.field) return true; // always keep headers/section labels
      if (row.type === 'total') return true; // always keep totals
      return hasAnyValue(displayedReports, row.field);
    });
  }, [allRows, displayedReports]);

  const renderCell = (report: Report, field: string | null, columnIndex: number) => {
    if (!field) return null;
    const value = report.balanceSheet?.[field as keyof BalanceSheetData];
    const previousReport = displayedReports[columnIndex + 1];
    let changeIndicator: ChangeIndicator | null = null;
    if (mode !== 'single' && previousReport) {
      changeIndicator = calculateChange(value, previousReport.balanceSheet?.[field as keyof BalanceSheetData]);
    }
    return (
      <div className="flex items-center justify-end gap-2">
        <span>{formatValue(value)}</span>
        {changeIndicator && (
          <span className={`flex items-center text-xs font-medium ${changeIndicator.color}`}>
            {changeIndicator.direction === 'up' && <TrendingUp className="h-3 w-3 mr-0.5" />}
            {changeIndicator.direction === 'down' && <TrendingDown className="h-3 w-3 mr-0.5" />}
            {changeIndicator.direction === 'neutral' && <Minus className="h-3 w-3 mr-0.5" />}
            {Math.abs(changeIndicator.percentage).toFixed(1)}%
          </span>
        )}
      </div>
    );
  };

  const getRowClass = (type: string) => {
    switch (type) {
      case 'header':   return 'bg-neutral-100 font-bold text-neutral-900';
      case 'category': return 'bg-primary-50 font-semibold text-primary-900';
      case 'total':    return 'bg-neutral-50 font-bold text-neutral-900 border-t-2 border-neutral-300';
      case 'item':     return 'text-neutral-700';
      default:         return '';
    }
  };

  const getLabelClass = (type: string) => {
    switch (type) {
      case 'header':   return 'py-3 px-4';
      case 'category': return 'py-2 px-4 pl-6';
      case 'total':    return 'py-3 px-4 pl-6';
      case 'item':     return 'py-2 px-4 pl-10';
      default:         return 'py-2 px-4';
    }
  };

  if (displayedReports.length === 0) {
    return (
      <Card>
        <CardBody className="text-center py-8 text-neutral-600">No balance sheet data available</CardBody>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">
              Balance Sheet {mode === 'single' ? 'Detail' : 'Comparison'}
            </h3>
            <p className="text-sm text-neutral-600 mt-1">
              {mode === 'annual' && `Showing ${displayedReports.length} Annual Report${displayedReports.length > 1 ? 's' : ''}`}
              {mode === 'quarterly' && `Showing ${displayedReports.length} Quarter${displayedReports.length > 1 ? 's' : ''}`}
              {mode === 'single' && displayedReports[0]?.period}
            </p>
          </div>
          {mode !== 'single' && (
            <div className="text-xs text-neutral-500">
              <span className="text-success-600 font-medium">↑ Increase</span>
              {' · '}
              <span className="text-error-600 font-medium">↓ Decrease</span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardBody className="p-0">
        {/* Custom styled scrollable container — thin scrollbar */}
        <div
          className="overflow-x-auto"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#cbd5e1 transparent',
          }}
        >
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white border-b-2 border-neutral-300 z-10">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-neutral-700 min-w-[220px]">
                  Category
                </th>
                {displayedReports.map((report, idx) => (
                  <th key={report.reportId} className="text-right py-3 px-4 font-semibold text-neutral-700 min-w-[140px]">
                    <div className="flex flex-col items-end">
                      <span>{report.period}</span>
                      {idx === 0 && mode !== 'single' && (
                        <span className="text-xs font-normal text-primary-600">Latest</span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => {
                if (!row.field) {
                  return (
                    <tr key={rowIndex} className={getRowClass(row.type)}>
                      <td colSpan={displayedReports.length + 1} className={getLabelClass(row.type)}>
                        {row.label}
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={rowIndex} className={`${getRowClass(row.type)} hover:bg-neutral-50`}>
                    <td className={getLabelClass(row.type)}>{row.label}</td>
                    {displayedReports.map((report, colIndex) => (
                      <td key={report.reportId} className="py-2 px-4 text-right">
                        {renderCell(report, row.field, colIndex)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}
